import { seoLinks, ogImage } from "@/lib/seo";
import { InlineCta } from "@/components/site/InlineCta";
import { createFileRoute, Link } from "@tanstack/react-router";

import { CtaBand, PageHero, Prose } from "../components/site/Page";
import { GuideLinks } from "../components/site/Guides";
import { KnowledgeHubNote } from "../components/site/KnowledgeHubNote";
import { NewcomerNote } from "../components/site/NewcomerNote";

const title = "NIE, empadronamiento och sjukvård i Spanien";
const description =
  "Översiktlig guide till NIE-nummer, folkbokföring (empadronamiento) och sjukvård för svenska familjer som flyttar till Spanien.";

export const Route = createFileRoute("/nie-och-folkbokforing-i-spanien")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:locale", content: "sv_SE" },
      ...ogImage(),
    ],
    links: seoLinks("/nie-och-folkbokforing-i-spanien"),
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <PageHero
        eyebrow="Guide"
        title="NIE, folkbokföring och sjukvård"
        intro="En översikt över de administrativa bitar som brukar dyka upp när en familj bosätter sig i Spanien. Regler och rutiner ändras över tid – kontrollera alltid aktuella uppgifter hos ansvarig myndighet."
      />

      <NewcomerNote />

      <Prose>
        <h2>Läs det här som en karta, inte som ett facit</h2>
        <p>
          Vi är en skola, inte en juridisk rådgivare. Syftet med den här sidan är att ge er
          orienteringen: vilka begrepp som dyker upp, ungefär i vilken ordning de kommer och hur de
          hänger ihop med skolstarten. Den detaljerade genomgången – ärende för ärende, med
          handlingar, tidsbokning och vanliga fallgropar – ligger samlad i vår kunskapsbank, så att
          vi kan hålla den uppdaterad på ett ställe i stället för att skriva samma sak två gånger.
        </p>

        <h2>Begreppen ni kommer att möta</h2>
        <ul>
          <li>
            <strong>NIE</strong> – det spanska identitets- och skattenumret. Det första steget,
            eftersom bostad, bankkonto och försäkringar hänger på det. Även barn kan behöva ett NIE
            beroende på familjens situation.
          </li>
          <li>
            <strong>Empadronamiento (padrón)</strong> – registrering i kommunens invånarregister på
            den adress ni faktiskt bor på. Efterfrågas i väldigt många sammanhang och behöver
            uppdateras om ni flyttar, till exempel när ni går från hyrt till köpt boende.
          </li>
          <li>
            <strong>Certificado de registro / TIE</strong> – registrering som EU-medborgare
            respektive uppehållskort för den som inte är EU-medborgare.
          </li>
          <li>
            <strong>Sjukvård</strong> – offentlig vård, privat försäkring eller en kombination,
            beroende på om ni arbetar i Spanien, är utsända, pensionärer eller självförsörjande.
          </li>
          <li>
            <strong>Utflyttningsanmälan</strong> – vid permanent flytt anmäls den normalt till
            Skatteverket, vilket påverkar folkbokföring, skatt och vissa förmåner.
          </li>
        </ul>
        <p>
          Många familjer anlitar en gestoría – ett lokalt administrativt ombud – för de tyngre
          ärendena. Det kostar några hundra euro och sparar ofta veckor, särskilt om ni ännu inte
          pratar spanska.
        </p>
      </Prose>

      <div className="mx-auto max-w-3xl px-5">
        <InlineCta locale="sv" />
      </div>

      <Prose>

        <h2>Det skolan behöver från er</h2>
        <p>
          Oavsett vilken väg ni väljer i vården: se till att vårdfrågan är löst innan skolstart och
          lämna kontaktuppgifter samt information om allergier, mediciner eller särskilda behov till
          oss. Vårt elevhälsoteam arbetar med både fysisk och psykosocial hälsa och behöver rätt
          underlag från början. Till ansökan hör också barnens senaste betyg eller omdömen och
          vaccinationsuppgifter.
        </p>

        <h2>Hela genomgången finns i kunskapsbanken</h2>
        <p>
          I materialet <em>Myndigheter och papper: NIE, TIE, padrón och skola</em> går vi igenom hela
          kedjan steg för steg: tidsbokning (cita previa) när allt ser fullbokat ut, vad padrón
          faktiskt låser upp, de fyra vägarna in i sjukvården inklusive EU-sjukkortet, vilka
          handlingar ni behöver ta med från Sverige och när apostille eller auktoriserad översättning
          krävs.
        </p>
        <p>
          <Link
            className="text-primary link-quiet"
            to="/kunskapsbank/$slug"
            params={{ slug: "myndigheter-och-papper" }}
          >
            Öppna materialet i kunskapsbanken
          </Link>{" "}
          – eller läs vidare i vår guide om att{" "}
          <Link
            className="text-primary link-quiet"
            to="/flytta-till-spanien-med-barn"
          >
            flytta till Spanien med barn
          </Link>
          .
        </p>
      </Prose>


      <KnowledgeHubNote resource="myndigheter-och-papper" />


      <GuideLinks current="/nie-och-folkbokforing-i-spanien" />
      <CtaBand />
      <div className="h-20" />
    </>
  );
}
